import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { TableColumn } from "react-data-table-component";
import {
  Badge,
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";

import {
  AppTable,
  ColumnHeader,
  DateColumn,
  TextColumn,
} from "../../components";
import { LiveLectureBookingAdminDto } from "../../interfaces";
import { useGetLiveLectureBookingsQuery } from "../../store/api/liveLectureApi";

type TProps = {
  liveLectureId?: string | null;
  show: boolean;
  hideCapacityLimits?: boolean;
  onCloseClick: () => void;
};

export const LiveLectureBookingsModal: React.FC<TProps> = ({
  liveLectureId,
  show,
  hideCapacityLimits = false,
  onCloseClick,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "ar" ? "ar" : "en";

  const { data, isLoading, isFetching, refetch } =
    useGetLiveLectureBookingsQuery(liveLectureId as string, {
      skip: !show || !liveLectureId,
    });

  const getUserName = (name?: { en: string; ar: string }) =>
    name ? name[lang] : "-";

  const getBookingStatusColor = (status: string) => {
    switch (status) {
      case "CONFIRMED":
      case "ATTENDED":
        return "success";
      case "PENDING_PAYMENT":
        return "warning";
      case "CANCELLED":
      case "REFUNDED":
        return "danger";
      case "NO_SHOW":
        return "secondary";
      default:
        return "primary";
    }
  };

  const columns: TableColumn<LiveLectureBookingAdminDto>[] = [
    {
      name: <ColumnHeader title="student" />,
      cell: (row) => (
        <div>
          <div className="fw-medium">
            {getUserName(row.student?.name) || "-"}
          </div>
          {row.student?.phone && (
            <div className="text-muted small">{row.student.phone}</div>
          )}
        </div>
      ),
    },
    {
      name: <ColumnHeader title="bookedBy" />,
      cell: (row) => (
        <div>
          <div>{getUserName(row.bookedBy?.name) || "-"}</div>
          {row.bookedBy?.phone && (
            <div className="text-muted small">{row.bookedBy.phone}</div>
          )}
        </div>
      ),
    },
    {
      name: <ColumnHeader title="status" />,
      cell: (row) => (
        <Badge color={getBookingStatusColor(row.status)}>
          {t(`liveLectureBookingStatus_${row.status}`)}
        </Badge>
      ),
    },
    {
      name: <ColumnHeader title="bookedAt" />,
      cell: (row) => <DateColumn date={row.createdAt} />,
    },
    {
      name: <ColumnHeader title="payment" />,
      cell: (row) => (
        <TextColumn
          text={
            row.bookingPaymentMode
              ? t(`liveLectureBookingPaymentMode_${row.bookingPaymentMode}`)
              : "-"
          }
        />
      ),
    },
    {
      name: <ColumnHeader title="total" />,
      cell: (row) => (
        <TextColumn text={row.total > 0 ? row.total.toFixed(2) : "-"} />
      ),
    },
    {
      name: <ColumnHeader title="participants" />,
      cell: (row) => (
        <TextColumn
          text={
            row.participants?.length
              ? row.participants
                  .map(
                    (participant) =>
                      participant.participantName ||
                      participant.participantEmail ||
                      "-",
                  )
                  .join(", ")
              : "-"
          }
          isNeedToTranslate={false}
        />
      ),
    },
  ];

  return (
    <Modal isOpen={show} toggle={onCloseClick} centered size="xl">
      <ModalHeader toggle={onCloseClick}>
        {t("liveLectureBookings")}
      </ModalHeader>
      <ModalBody>
        {isLoading ? (
          <div className="text-center py-4">
            <Spinner />
          </div>
        ) : data ? (
          <React.Fragment>
            <div className="mb-3">
              <h5 className="mb-1">{getUserName(data.title)}</h5>
              <div className="text-muted small">
                {moment(data.startAt).format("lll")} -{" "}
                {moment(data.endAt).format("HH:mm")}
              </div>
            </div>

            <Row className="g-3 mb-4">
              <Col sm={6} md={hideCapacityLimits ? 6 : 3}>
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small">{t("bookedCount")}</div>
                  <div className="fs-20 fw-semibold">
                    {hideCapacityLimits
                      ? data.bookedCount
                      : `${data.bookedCount}/${data.maxParticipants ?? "-"}`}
                  </div>
                </div>
              </Col>
              {!hideCapacityLimits && (
                <Col sm={6} md={3}>
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small">{t("availableSeats")}</div>
                    <div className="fs-20 fw-semibold">{data.availableSeats}</div>
                  </div>
                </Col>
              )}
              <Col sm={6} md={hideCapacityLimits ? 6 : 3}>
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small">{t("totalBookings")}</div>
                  <div className="fs-20 fw-semibold">
                    {data.bookings.length}
                  </div>
                </div>
              </Col>
              {!hideCapacityLimits && (
                <Col sm={6} md={3}>
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small">{t("maxParticipants")}</div>
                    <div className="fs-20 fw-semibold">
                      {data.maxParticipants}
                    </div>
                  </div>
                </Col>
              )}
            </Row>

            {data.bookings.length === 0 ? (
              <div className="text-center text-muted py-4">
                {t("noLiveLectureBookings")}
              </div>
            ) : (
              <AppTable
                columns={columns}
                data={data.bookings}
                loading={isFetching}
                pagination={false}
              />
            )}
          </React.Fragment>
        ) : (
          <div className="text-center text-muted py-4">
            {t("noLiveLectureBookings")}
          </div>
        )}
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between">
        <Button
          color="success"
          onClick={() => refetch()}
          disabled={isFetching || !liveLectureId}
        >
          {isFetching ? (
            <Spinner size="sm" />
          ) : (
            <React.Fragment>{t("refresh")}</React.Fragment>
          )}
        </Button>
        <Link
          to="#"
          onClick={onCloseClick}
          className="btn btn-link link-success fw-medium"
        >
          <i className="ri-close-line me-1 align-middle"></i> {t("close")}
        </Link>
      </ModalFooter>
    </Modal>
  );
};
